import React, { useState, useEffect } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import { useLanguage } from '../contexts/LanguageContext';
import { CheckCircle, Star, ExternalLink, ArrowLeft, X } from 'lucide-react';
import { Button } from '../components/ui/button';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const SUCCESS_TEXT = {
  en: { processing: "Processing Payment...", wait: "Please wait while we confirm your payment", paid: "Amount Paid", tip: "Tip included", redirect: "Redirecting to review in", sec: "s", back: "Back to Menu", expired: "Payment Expired", expired_msg: "Your payment session has expired. Please try again.", timeout: "Status Check Timeout", timeout_msg: "We couldn't confirm your payment. Please check your email for confirmation.", return: "Return to Menu" },
  fr: { processing: "Paiement en cours...", wait: "Veuillez patienter pendant la confirmation", paid: "Montant paye", tip: "Pourboire inclus", redirect: "Redirection vers l'avis dans", sec: "s", back: "Retour au menu", expired: "Paiement expire", expired_msg: "Votre session de paiement a expire. Veuillez reessayer.", timeout: "Delai depasse", timeout_msg: "Impossible de confirmer votre paiement. Verifiez vos emails.", return: "Retour au menu" },
  th: { processing: "กำลังดำเนินการชำระเงิน...", wait: "กรุณารอขณะยืนยันการชำระเงิน", paid: "จำนวนที่ชำระ", tip: "รวมทิป", redirect: "เปลี่ยนเส้นทางไปรีวิวใน", sec: "วิ", back: "กลับไปที่เมนู", expired: "การชำระเงินหมดอายุ", expired_msg: "เซสชันการชำระเงินหมดอายุ กรุณาลองอีกครั้ง", timeout: "หมดเวลาตรวจสอบ", timeout_msg: "ไม่สามารถยืนยันการชำระเงินได้ กรุณาตรวจสอบอีเมล", return: "กลับไปที่เมนู" },
  ru: { processing: "Обработка платежа...", wait: "Подождите, пока мы подтвердим оплату", paid: "Оплачено", tip: "Включая чаевые", redirect: "Переход к отзыву через", sec: "с", back: "Вернуться в меню", expired: "Платеж просрочен", expired_msg: "Сессия оплаты истекла. Попробуйте снова.", timeout: "Тайм-аут проверки", timeout_msg: "Не удалось подтвердить оплату. Проверьте электронную почту.", return: "Вернуться в меню" },
  de: { processing: "Zahlung wird verarbeitet...", wait: "Bitte warten Sie auf die Bestatigung", paid: "Bezahlter Betrag", tip: "Trinkgeld inklusive", redirect: "Weiterleitung zur Bewertung in", sec: "s", back: "Zuruck zum Menu", expired: "Zahlung abgelaufen", expired_msg: "Ihre Zahlungssitzung ist abgelaufen. Bitte versuchen Sie es erneut.", timeout: "Zeituberschreitung", timeout_msg: "Zahlung konnte nicht bestatigt werden. Prufen Sie Ihre E-Mail.", return: "Zuruck zum Menu" },
  es: { processing: "Procesando pago...", wait: "Espere mientras confirmamos su pago", paid: "Monto pagado", tip: "Propina incluida", redirect: "Redirigiendo a resena en", sec: "s", back: "Volver al menu", expired: "Pago expirado", expired_msg: "Su sesion de pago ha expirado. Intente de nuevo.", timeout: "Tiempo agotado", timeout_msg: "No pudimos confirmar su pago. Revise su correo.", return: "Volver al menu" },
  ar: { processing: "جاري معالجة الدفع...", wait: "يرجى الانتظار أثناء تأكيد الدفع", paid: "المبلغ المدفوع", tip: "تشمل الإكرامية", redirect: "إعادة التوجيه إلى التقييم خلال", sec: "ث", back: "العودة إلى القائمة", expired: "انتهت صلاحية الدفع", expired_msg: "انتهت جلسة الدفع. يرجى المحاولة مرة أخرى.", timeout: "انتهت مهلة التحقق", timeout_msg: "لم نتمكن من تأكيد الدفع. يرجى التحقق من بريدك الإلكتروني.", return: "العودة إلى القائمة" },
  zh: { processing: "正在处理付款...", wait: "请等待确认您的付款", paid: "已支付金额", tip: "含小费", redirect: "将在以下时间后跳转到评价页面", sec: "秒", back: "返回菜单", expired: "付款已过期", expired_msg: "您的付款会话已过期，请重试。", timeout: "状态检查超时", timeout_msg: "无法确认您的付款，请检查您的电子邮件。", return: "返回菜单" },
};

const PaymentSuccess = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const { t, language } = useLanguage();
  const [status, setStatus] = useState('checking');
  const [paymentInfo, setPaymentInfo] = useState(null);
  const [countdown, setCountdown] = useState(8);

  const sessionId = searchParams.get('session_id');
  const tableId = searchParams.get('table_id');
  const txt = SUCCESS_TEXT[language] || SUCCESS_TEXT.en;

  useEffect(() => {
    if (sessionId) pollPaymentStatus();
  }, [sessionId]);

  useEffect(() => {
    if (status !== 'success' || !paymentInfo?.google_review_url) return;
    if (countdown <= 0) {
      window.location.href = paymentInfo.google_review_url;
      return;
    }
    const timer = setTimeout(() => setCountdown(c => c - 1), 1000);
    return () => clearTimeout(timer);
  }, [status, countdown, paymentInfo]);

  const pollPaymentStatus = async (attempts = 0) => {
    if (attempts >= 10) { setStatus('timeout'); return; }
    try {
      const response = await axios.get(`${API}/checkout/status/${sessionId}`);
      if (response.data.payment_status === 'paid') {
        setStatus('success');
        setPaymentInfo(response.data);
      } else if (response.data.status === 'expired') {
        setStatus('expired');
      } else {
        setTimeout(() => pollPaymentStatus(attempts + 1), 2000);
      }
    } catch (error) {
      setTimeout(() => pollPaymentStatus(attempts + 1), 2000);
    }
  };

  const formatAmount = (amount, currency) => {
    if (currency === 'thb') return `฿${(amount / 100).toLocaleString()}`;
    return `${currency?.toUpperCase()} ${(amount / 100).toFixed(2)}`;
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-[#0A422D] to-[#083323] flex items-center justify-center p-4">
      <div className="bg-white rounded-3xl p-8 max-w-md w-full text-center shadow-2xl animate-slide-up">
        {status === 'checking' && (
          <>
            <div className="w-20 h-20 border-4 border-[#E16833] border-t-transparent rounded-full animate-spin mx-auto mb-6" />
            <h1 className="text-2xl font-bold text-[#2D241D] mb-2">{txt.processing}</h1>
            <p className="text-[#6E5E52]">{txt.wait}</p>
          </>
        )}

        {status === 'success' && (
          <>
            <div className="w-20 h-20 bg-[#2D6A4F] rounded-full flex items-center justify-center mx-auto mb-6 animate-pulse-soft">
              <CheckCircle className="w-12 h-12 text-white" />
            </div>
            <h1 className="text-3xl font-bold text-[#2D241D] mb-2" data-testid="payment-success-title">
              {t('thank_you')}
            </h1>
            <p className="text-[#6E5E52] mb-4">{t('payment_success')}</p>

            {paymentInfo && (
              <div className="bg-[#F3EDE5] rounded-2xl p-4 mb-6">
                <p className="text-[#6E5E52] text-sm mb-1">{txt.paid}</p>
                <p className="text-2xl font-bold text-[#E16833]">
                  {formatAmount(paymentInfo.amount_total, paymentInfo.currency)}
                </p>
                {paymentInfo.tip_amount > 0 && (
                  <p className="text-sm text-[#2D6A4F] font-medium mt-1" data-testid="tip-display">
                    {txt.tip}: ฿{Math.round(paymentInfo.tip_amount).toLocaleString()}
                  </p>
                )}
              </div>
            )}

            <div className="space-y-3">
              {/* Countdown */}
              <div className="flex items-center justify-center gap-2 text-sm text-[#6E5E52]">
                <span>{txt.redirect}</span>
                <span className="w-8 h-8 bg-[#E16833] text-white rounded-full flex items-center justify-center font-bold text-sm">
                  {countdown}
                </span>
                <span>{txt.sec}</span>
              </div>

              <Button
                onClick={() => window.location.href = paymentInfo?.google_review_url || '#'}
                className="w-full h-12 btn-primary rounded-full font-semibold"
                data-testid="leave-review-btn"
              >
                <Star className="w-5 h-5 mr-2" />
                {t('leave_review')}
                <ExternalLink className="w-4 h-4 ml-2" />
              </Button>

              <Button
                onClick={() => navigate(`/table/${tableId}`)}
                variant="outline"
                className="w-full h-12 rounded-full border-[#E5DCD3] text-[#6E5E52]"
                data-testid="back-to-menu-btn"
              >
                <ArrowLeft className="w-4 h-4 mr-2" />
                {txt.back}
              </Button>
            </div>
          </>
        )}

        {status === 'expired' && (
          <>
            <div className="w-20 h-20 bg-[#E63946] rounded-full flex items-center justify-center mx-auto mb-6">
              <X className="w-10 h-10 text-white" />
            </div>
            <h1 className="text-2xl font-bold text-[#2D241D] mb-2">{txt.expired}</h1>
            <p className="text-[#6E5E52] mb-6">{txt.expired_msg}</p>
            <Button onClick={() => navigate(`/table/${tableId}`)} className="w-full h-12 btn-primary rounded-full">
              {txt.return}
            </Button>
          </>
        )}

        {status === 'timeout' && (
          <>
            <div className="w-20 h-20 bg-[#E9C46A] rounded-full flex items-center justify-center mx-auto mb-6">
              <span className="text-3xl text-[#2D241D] font-bold">?</span>
            </div>
            <h1 className="text-2xl font-bold text-[#2D241D] mb-2">{txt.timeout}</h1>
            <p className="text-[#6E5E52] mb-6">{txt.timeout_msg}</p>
            <Button onClick={() => navigate(`/table/${tableId}`)} className="w-full h-12 btn-primary rounded-full">
              {txt.return}
            </Button>
          </>
        )}
      </div>
    </div>
  );
};

export default PaymentSuccess;
